import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import logo from '../images/common/Logo_Rond.png';
import { useSeason } from '../contexts/SeasonContext';
import { trackInscriptionClick } from '../utils/analytics';

const navItems = [
  { key: 'club',        label: 'Club' },
  { key: 'inscription', label: 'Inscription' },
  { key: 'creneaux',   label: 'Créneaux' },
  { key: 'tarifs',     label: 'Tarifs' },
  // { key: 'entrainements',   label: 'Entraineurs' },
  { key: 'agenda',     label: 'Agenda' },
  { key: 'boutique',   label: 'Boutique' },
  { key: 'regles',     label: 'Règles' },
  { key: 'acces',      label: 'Accès' },
  { key: 'contact',    label: 'Contact' },
  { key: 'FAQ',        label: 'FAQ' },
];

const Header = ({ setCurrentSection, currentSection, isDark = false }) => {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const { season } = useSeason();
  const inscriptionsOpen = season?.inscriptions_open ?? true;

  const go = (key) => {
    window.scrollTo({ top: 0, behavior: 'instant' });
    setCurrentSection(key);
    setMenuOpen(false);
  };

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 32);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    document.body.style.overflow = menuOpen ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [menuOpen]);

  /* When hero is dark and not yet scrolled, use light-on-dark styling */
  const dark = isDark && !scrolled;

  return (
    <>
      <header
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          scrolled
            ? 'bg-white/90 backdrop-blur-md border-b border-stone-100 py-3 shadow-[0_1px_0_rgba(0,0,0,0.06)]'
            : 'bg-transparent py-5'
        }`}
      >
        <div className="max-w-6xl mx-auto px-5 flex items-center justify-between">
          {/* Logo */}
          <Link
            to="/accueil"
            onClick={() => go('accueil')}
            className="flex items-center gap-2.5 group shrink-0"
          >
            <div className="w-9 h-9 shrink-0">
              <img src={logo} alt="BCE" className="w-full h-full object-contain drop-shadow-sm" />
            </div>
            <span className={`font-bold text-lg tracking-tight transition-colors ${
              dark
                ? 'text-white group-hover:text-white/80'
                : 'text-stone-950 group-hover:text-brand-800'
            }`}>
              BCE
            </span>
          </Link>

          {/* Desktop nav */}
          <nav className="hidden lg:flex items-center gap-0.5">
            {navItems.map(({ key, label }) => (
              <Link
                key={key}
                to={`/${key}`}
                onClick={() => go(key)}
                className={`px-3.5 py-1.5 rounded-full text-sm font-medium transition-all duration-150 ${
                  dark
                    ? currentSection === key
                      ? 'text-white bg-white/15'
                      : 'text-white/60 hover:text-white hover:bg-white/10'
                    : currentSection === key
                      ? 'text-brand-800 bg-brand-50'
                      : 'text-stone-500 hover:text-stone-950 hover:bg-stone-50'
                }`}
              >
                {label}
              </Link>
            ))}
          </nav>

          {/* Desktop CTA */}
          {inscriptionsOpen && (
            <div className="hidden lg:flex items-center gap-3">
              <Link
                to="/inscription"
                onClick={() => { go('inscription'); trackInscriptionClick(); }}
                className={`btn text-sm py-2 px-5 transition-all ${
                  dark
                    ? 'border-2 border-white/30 text-white hover:bg-white/10'
                    : 'btn-brand'
                }`}
              >
                S'inscrire
              </Link>
            </div>
          )}

          {/* Mobile burger */}
          <button
            onClick={() => setMenuOpen(!menuOpen)}
            className={`lg:hidden p-2 rounded-lg transition-colors ${
              dark ? 'text-white/80 hover:bg-white/10' : 'text-stone-600 hover:bg-stone-100'
            }`}
            aria-label="Menu"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round"
                d={menuOpen ? 'M6 18L18 6M6 6l12 12' : 'M3 6h18M3 12h18M3 18h18'} />
            </svg>
          </button>
        </div>
      </header>

      {/* Mobile menu backdrop */}
      {menuOpen && (
        <div className="fixed inset-0 z-40 bg-black/20 backdrop-blur-sm lg:hidden" onClick={() => setMenuOpen(false)} />
      )}

      {/* Mobile drawer */}
      <div className={`fixed top-0 right-0 bottom-0 z-50 w-72 bg-white shadow-2xl lg:hidden transition-transform duration-300 ${menuOpen ? 'translate-x-0' : 'translate-x-full'}`}>
        <div className="flex items-center justify-between px-5 py-4 border-b border-stone-100">
          <div className="flex items-center gap-2">
            <img src={logo} alt="BCE" className="w-8 h-8 object-contain" />
            <span className="font-bold text-stone-950">BCE</span>
          </div>
          <button onClick={() => setMenuOpen(false)} className="p-1.5 rounded-lg text-stone-400 hover:bg-stone-100 transition-colors">
            <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <div className="p-4 space-y-0.5 overflow-y-auto">
          <Link
            to="/accueil"
            onClick={() => go('accueil')}
            className={`block px-4 py-2.5 rounded-xl text-sm font-medium transition-colors ${currentSection === 'accueil' ? 'bg-brand-50 text-brand-800' : 'text-stone-600 hover:bg-stone-50 hover:text-stone-950'}`}
          >
            Accueil
          </Link>
          {navItems.map(({ key, label }) => (
            <Link
              key={key}
              to={`/${key}`}
              onClick={() => go(key)}
              className={`block px-4 py-2.5 rounded-xl text-sm font-medium transition-colors ${currentSection === key ? 'bg-brand-50 text-brand-800' : 'text-stone-600 hover:bg-stone-50 hover:text-stone-950'}`}
            >
              {label}
            </Link>
          ))}
        </div>

        {inscriptionsOpen && (
          <div className="absolute bottom-0 left-0 right-0 p-4 border-t border-stone-100">
            <Link
              to="/inscription"
              onClick={() => { go('inscription'); trackInscriptionClick(); }}
              className="btn-brand w-full text-sm py-2.5"
            >
              S'inscrire
            </Link>
          </div>
        )}
      </div>
    </>
  );
};

export default Header;
